import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import {
  ActivityEventType,
  type WorkoutActivityEvent,
  type WorkoutActivityEventWithFeedback,
} from "@fitness-app/data-models/entities/ProgramActivity";
import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import { WorkoutSource } from "@fitness-app/data-models/entities/ClientWorkout";
import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";
import { ExerciseRecordingStatus } from "@fitness-app/data-models/entities/ExerciseRecording";
import {
  TrainingStatus,
  type ClientTrainingProgram,
  type ClientTrainingProgramDetails,
  type ExerciseInWorkout,
  type ProgramWorkout,
} from "@fitness-app/data-models/entities/TrainingProgram";
import {
  WorkoutService,
  type ProgramWorkoutWithMetadata,
} from "@fitness-app/data-models/domain/services/WorkoutService";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { supabase } from "~/store/initializeStore";
import {
  forgetSimulatorWrite,
  getSimulatorWrite,
  logSimulatorWrite,
  SIMULATOR_TABLE,
  wasCreatedBySimulator,
} from "./simulatorWriteLog";

dayjs.extend(isoWeek);

/**
 * Placeholder zamiast prawdziwego pliku. Scoring wyzwania patrzy na status nagrania, nie na
 * treść wideo, a wgrywanie filmu z admina ma własną, istniejącą już ścieżkę.
 */
const SIMULATED_RECORDING_URL = "simulator://recording";

const SELECT_WITH_WORKOUT_RATE =
  "*, workoutRate:feedbackId(data->activityId, data->workoutId, data->rate, data->comment, data->trainingStatus, data->programId)";

export type SimulatorExercise = {
  /** Id ćwiczenia w treningu — po nim odhaczamy. */
  id: string;
  name: string;
  done: boolean;
  numberOfSeries: number;
  requiresRecording: boolean;
  /**
   * Id ćwiczenia w katalogu. Nagranie wiąże się z ćwiczeniem właśnie po nim
   * (`exercise_recording.parentExerciseId`), a nie po id pozycji w treningu.
   */
  catalogExerciseId: string | null;
};

export type SimulatorWorkout = {
  /** Id aktywności, jeśli trening już istnieje w `client_workout_activity`. */
  activityId: string | null;
  /** Id pomiaru z oceną treningu, jeśli klientka (albo symulator) już ją wystawiła. */
  feedbackId: string | null;
  /** Wystawiona ocena 1–5, jeśli jest. */
  rate: number | null;
  workoutId: string;
  workout: ProgramWorkout;
  name: string;
  /** Skąd trening się wziął: zapisana aktywność czy dopiero harmonogram planu. */
  source: WorkoutSource;
  exercises: SimulatorExercise[];
};

const allExercisesOf = (workout: ProgramWorkout): ExerciseInWorkout[] => [
  ...(workout.preWorkout ?? []),
  ...(workout.exercises ?? []),
  ...(workout.postWorkout ?? []),
];

const toSimulatorWorkout = (workout: ProgramWorkoutWithMetadata): SimulatorWorkout => ({
  activityId: workout.activityId ?? null,
  feedbackId: workout.feedbackId ?? null,
  rate: workout.workoutRate?.rate ?? null,
  workoutId: workout.id,
  workout,
  name: workout.name || "Trening",
  source: workout.source,
  exercises: allExercisesOf(workout).map((exercise) => ({
    id: exercise.id,
    name: exercise.exercise?.name ?? "Ćwiczenie",
    done: exercise.status === TrainingStatus.FULFILLED || exercise.status === TrainingStatus.PARTIALLY_FULFILLED,
    numberOfSeries: exercise.numberOfSeries ?? 0,
    requiresRecording: Boolean(exercise.requireExerciseVideoRecording),
    catalogExerciseId: exercise.exercise?.id ?? null,
  })),
});

/** Id ćwiczeń aktualnie odhaczonych — punkt wyjścia do przełączania pojedynczych pozycji. */
export const doneExerciseIds = (item: SimulatorWorkout): Set<string> =>
  new Set(item.exercises.filter((exercise) => exercise.done).map((exercise) => exercise.id));

export const allExerciseIds = (item: SimulatorWorkout): Set<string> =>
  new Set(item.exercises.map((exercise) => exercise.id));

/**
 * Treningi, które klientka widzi w danym dniu.
 *
 * Liczy je `WorkoutService.getWorkoutsForDay` — ta sama funkcja, której używa aplikacja klienta.
 * To istotne: dzień składa się z zapisanych aktywności ORAZ z treningów wynikających z
 * harmonogramu planu, które nie mają jeszcze wiersza w `client_workout_activity`. Czytanie samej
 * tabeli aktywności gubiło te drugie, przez co dzień z dwoma treningami pokazywał tylko ten
 * już zrobiony.
 */
export const listWorkoutsForDay = async (
  traineeId: string,
  activeProgramId: string | null,
  date: string,
): Promise<SimulatorWorkout[]> => {
  const [activitiesResult, programResult] = await Promise.all([
    supabase
      .from("client_workout_activity")
      // Ocena treningu leży w osobnym pomiarze, wskazywanym przez `feedbackId` — ten sam join,
      // którego używa aplikacja klientki, żeby pokazać wystawioną gwiazdkę.
      .select(SELECT_WITH_WORKOUT_RATE)
      .eq("traineeId", traineeId)
      .eq("eventDate", date),
    activeProgramId
      ? supabase
          .from("client_training_program")
          .select("*, details:client_training_program_details (*)")
          .eq("id", activeProgramId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);

  if (activitiesResult.error) {
    throw new Error(activitiesResult.error.message);
  }

  const activities = (activitiesResult.data ?? []) as unknown as WorkoutActivityEventWithFeedback[];

  if (programResult.error || !programResult.data) {
    // Bez aktywnego planu zostają same zapisane aktywności — harmonogramu nie ma z czego wziąć.
    return activities.map((activity) =>
      toSimulatorWorkout({
        ...(activity.data as ProgramWorkout),
        status: null,
        source: WorkoutSource.FromActivity,
        activityId: activity.id,
      }),
    );
  }

  const { details, ...plan } = programResult.data;
  const programDetails = (Array.isArray(details) ? details[0] : details) as unknown as ClientTrainingProgramDetails;

  return WorkoutService.getWorkoutsForDay({
    selectedDate: dayjs(date, "YYYY-MM-DD"),
    activitiesForSelectedDate: activities,
    clientProgram: plan as unknown as ClientTrainingProgram,
    programDetails,
  }).map(toSimulatorWorkout);
};

/**
 * Ustawia statusy ćwiczeń tak, żeby wykonane były dokładnie te z `completedIds`.
 *
 * `ClientActivityService.updateWorkoutActivity` liczy trening do tygodniowego licznika dopiero,
 * gdy KAŻDE ćwiczenie jest wykonane; częściowe odhaczenie podbija tylko dzienny
 * `exercisesCompleted`. Serie idą za statusem ćwiczenia — klientka kończąca ćwiczenie odhacza
 * je w komplecie.
 */
const applyExerciseStatuses = (
  workout: ProgramWorkout,
  completedIds: Set<string>,
  timestamp: string,
): ProgramWorkout => {
  const apply = (exercise: ExerciseInWorkout): ExerciseInWorkout => {
    const status = completedIds.has(exercise.id) ? TrainingStatus.FULFILLED : TrainingStatus.NEW;
    return {
      ...exercise,
      status,
      updatedAt: timestamp,
      set: Object.fromEntries(Object.entries(exercise.set ?? {}).map(([key, set]) => [key, { ...set, status }])),
    };
  };

  return {
    ...workout,
    updatedAt: timestamp,
    preWorkout: (workout.preWorkout ?? []).map(apply),
    exercises: (workout.exercises ?? []).map(apply),
    postWorkout: (workout.postWorkout ?? []).map(apply),
  };
};

type SetExercisesParams = {
  activityId: string;
  completedIds: Set<string>;
  traineeId: string;
  date: string;
  createdBy: string;
  mode: "single" | "bulk";
};

/** Przestawia odhaczenie ćwiczeń w istniejącej aktywności — np. w replikacji z planu. */
export const setWorkoutActivityExercises = async ({
  activityId,
  completedIds,
  traineeId,
  date,
  createdBy,
  mode,
}: SetExercisesParams): Promise<void> => {
  const { data: existing, error: fetchError } = await supabase
    .from("client_workout_activity")
    .select("id, data")
    .eq("id", activityId)
    .single();

  if (fetchError) {
    throw new Error(fetchError.message);
  }

  const timestamp = new Date().toISOString();
  const workout = existing.data as unknown as ProgramWorkout;

  const { error } = await supabase
    .from("client_workout_activity")
    .update({
      data: applyExerciseStatuses(workout, completedIds, timestamp) as never,
      updatedAt: timestamp,
    })
    .eq("id", activityId);

  if (error) {
    throw new Error(error.message);
  }

  // Uwaga: nawet przy pustym zbiorze wpis rejestru MUSI zostać. Payload aktywności jest już
  // nadpisany, więc skasowanie wpisu odcięłoby jedyną drogę powrotu do stanu sprzed symulatora.
  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.workoutActivity,
    rowId: activityId,
    eventDate: date,
    mode,
    // Aktywność istniała wcześniej (replikacja z planu albo wpis klientki) — symulator tylko
    // przestawia statusy, więc czyszczenie przywraca poprzedni payload zamiast kasować wiersz.
    op: "update",
    before: { data: workout },
    label: workout?.name ?? "Trening",
  });
};

type CreateParams = {
  workout: ProgramWorkout;
  /** Które ćwiczenia mają być wykonane; pominięcie oznacza cały trening. */
  completedIds?: Set<string>;
  programId: string | null;
  traineeId: string;
  userId: string | null;
  clientEmail: string;
  date: string;
  createdBy: string;
  mode: "single" | "bulk";
};

/**
 * Tworzy nową, w pełni wykonaną aktywność treningową na wskazany dzień.
 *
 * `replicatedFromSchedule: true` jest tu celowe: to ta sama ścieżka, którą idzie klientka
 * dodająca i kończąca trening w jednym kroku. Świeży INSERT z tą flagą liczy się do aktywności
 * TYLKO wtedy, gdy wszystkie ćwiczenia są wykonane — częściowe odhaczenie zakłada więc wiersz,
 * który jeszcze nie punktuje, i dopiero jego uzupełnienie (już jako UPDATE) wchodzi do liczników.
 */
export const createCompletedWorkoutActivity = async ({
  workout,
  completedIds,
  programId,
  traineeId,
  userId,
  clientEmail,
  date,
  createdBy,
  mode,
}: CreateParams): Promise<string> => {
  const eventDate = dayjs(date, "YYYY-MM-DD").hour(12);
  const timestamp = new Date().toISOString();
  const id = generateUniqId();

  const activity: WorkoutActivityEvent = {
    id,
    type: ActivityEventType.Workout,
    createdAt: timestamp,
    updatedAt: timestamp,
    clientEmail,
    traineeId,
    userId,
    data: {
      ...applyExerciseStatuses(
        workout,
        completedIds ?? new Set(allExercisesOf(workout).map((item) => item.id)),
        timestamp,
      ),
      replicatedFromSchedule: true,
    },
    eventDate: eventDate.format("YYYY-MM-DD"),
    eventMonth: eventDate.format("YYYY-MM"),
    eventWeek: `${eventDate.format("YYYY")}-${eventDate.isoWeek()}`,
    eventTimestamp: eventDate.toISOString(),
    programId,
    workoutId: workout.id,
    feedbackId: null,
    synced: true,
    metadata: { source: "postgres" },
  };

  // Czy wiersz już istnieje pod pełnym kluczem naturalnym — decyduje o tym, czy czyszczenie ma go
  // później skasować, czy przywrócić. `programId` bywa NULL, a upsert i tak wtedy trafia w konflikt,
  // więc dopasowanie musi traktować NULL jak wartość, a nie jak „nieznane".
  const existingQuery = supabase
    .from("client_workout_activity")
    .select("id, data, feedbackId")
    .eq("traineeId", traineeId)
    .eq("workoutId", workout.id)
    .eq("eventDate", eventDate.format("YYYY-MM-DD"));

  const { data: existing, error: existingError } = await (programId
    ? existingQuery.eq("programId", programId)
    : existingQuery.is("programId", null)
  ).maybeSingle();

  if (existingError) {
    throw new Error(`Nie udało się sprawdzić istniejącej aktywności: ${existingError.message}`);
  }

  const { data: saved, error } = await supabase
    .from("client_workout_activity")
    .upsert(
      (existing ? { ...activity, id: existing.id } : activity) as never,
      { onConflict: "traineeId,workoutId,eventDate,programId" },
    )
    .select("id")
    .single();

  if (error) {
    throw new Error(error.message);
  }

  const rowId = saved?.id ?? existing?.id ?? id;

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.workoutActivity,
    rowId,
    eventDate: date,
    mode,
    op: existing ? "update" : "insert",
    before: existing ? { data: existing.data, feedbackId: existing.feedbackId } : undefined,
    label: workout.name,
  });

  return rowId;
};

/** Treningi z aktywnego planu podopiecznej — źródło wyboru „co dziś zrobiła". */
export const fetchProgramWorkouts = async (
  programId: string,
): Promise<{ workouts: ProgramWorkout[]; startDate: string | null }> => {
  const { data, error } = await supabase
    .from("client_training_program")
    .select("id, startDate, details:client_training_program_details (workouts)")
    .eq("id", programId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  const details = Array.isArray(data.details) ? data.details[0] : data.details;

  return {
    workouts: ((details?.workouts ?? []) as unknown as ProgramWorkout[]).filter((workout) => !workout.isRestDay),
    startDate: data.startDate,
  };
};

export type SimulatorRecording = {
  id: string;
  status: ExerciseRecordingStatus;
  parentExerciseId: string | null;
  workoutId: string | null;
  exerciseName: string;
  metadata: Record<string, unknown> | null;
};

/**
 * Nagrania ćwiczeń zaplanowane na dany dzień.
 *
 * Wierszy nie tworzy klient ani admin — zakłada je workouts-service, gdy trening z
 * `requireExerciseVideoRecording` trafia do planu. Klientka tylko podmienia je na `recorded`,
 * wgrywając wideo, i to jest jedyna rzecz, którą symulator ma tu odwzorować.
 */
export const listRecordingsForDay = async (traineeId: string, date: string): Promise<SimulatorRecording[]> => {
  const { data, error } = await supabase
    .from("exercise_recording")
    // Relacja do `exercise` wisi na `parentExerciseId`, a nie na `exerciseId` — tego drugiego
    // PostgREST nie umie rozwiązać, bo nie ma na nim klucza obcego.
    .select("id, status, parentExerciseId, workoutId, metadata, exercise:parentExerciseId(name)")
    .eq("traineeId", traineeId)
    .eq("scheduledAt", date);

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []).map((row) => ({
    id: row.id,
    status: row.status as ExerciseRecordingStatus,
    parentExerciseId: row.parentExerciseId,
    workoutId: row.workoutId,
    exerciseName: (row.exercise as { name?: string } | null)?.name ?? "Ćwiczenie",
    metadata: (row.metadata ?? null) as Record<string, unknown> | null,
  }));
};

/**
 * Oznacza nagranie jako wgrane (albo cofa do `scheduled`).
 *
 * `url` dostaje placeholder zamiast prawdziwego pliku: scoring wyzwania patrzy wyłącznie na
 * status (`recorded` wystarcza wszędzie poza zadaniami na akceptację trenera, które wymagają
 * `rated`), a wgrywanie wideo z admina to osobna, istniejąca już ścieżka.
 */
export const setRecordingUploaded = async ({
  recording,
  traineeId,
  date,
  createdBy,
  uploaded,
  mode,
  url,
  path,
}: {
  recording: SimulatorRecording;
  traineeId: string;
  date: string;
  createdBy: string;
  uploaded: boolean;
  mode: "single" | "bulk";
  /** Adres wgranego pliku; bez niego status podstawiany jest placeholderem („mock"). */
  url?: string;
  path?: string;
}): Promise<void> => {
  const { error } = await supabase
    .from("exercise_recording")
    .update({
      status: uploaded ? ExerciseRecordingStatus.RECORDED : ExerciseRecordingStatus.SCHEDULED,
      url: uploaded ? (url ?? SIMULATED_RECORDING_URL) : null,
      metadata: uploaded
        ? {
            ...(recording.metadata ?? {}),
            path: path ?? null,
            addedAt: new Date().toISOString(),
            // `simulator` mówi, KTO ustawił nagranie (i tylko takie wolno potem cofnąć),
            // `simulated` — czy pod spodem jest prawdziwy plik, czy sam podstawiony status.
            simulator: true,
            simulated: !url,
          }
        : null,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", recording.id);

  if (error) {
    throw new Error(error.message);
  }

  if (!uploaded) {
    await forgetSimulatorWrite(SIMULATOR_TABLE.exerciseRecording, recording.id);
    return;
  }

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.exerciseRecording,
    rowId: recording.id,
    eventDate: date,
    mode,
    // Wiersz nagrania zakłada workouts-service — symulator go nie tworzy, tylko przestawia
    // status, więc czyszczenie musi wrócić do stanu sprzed, a nie usunąć zaplanowane nagranie.
    op: "update",
    before: { status: recording.status, url: null, metadata: recording.metadata ?? null },
    label: `Nagranie: ${recording.exerciseName}`,
  });
};

type RateParams = {
  item: SimulatorWorkout;
  rate: number;
  comment: string;
  programId: string | null;
  traineeId: string;
  userId: string | null;
  date: string;
  createdBy: string;
  mode: "single" | "bulk";
};

/**
 * Wystawia ocenę treningu — tę samą, którą klientka daje po jego zakończeniu.
 *
 * Ocena nie jest polem aktywności, tylko osobnym pomiarem typu `feedback_about_training`;
 * aktywność wskazuje go przez `feedbackId`. Stąd dwa zapisy: pomiar, a potem podpięcie go
 * do treningu. `creator: Client`, bo symulujemy klientkę, a nie notatkę trenerki.
 */
export const rateWorkout = async ({
  item,
  rate,
  comment,
  programId,
  traineeId,
  userId,
  date,
  createdBy,
  mode,
}: RateParams): Promise<void> => {
  if (!item.activityId) {
    throw new Error("Najpierw odhacz trening — ocenę wystawia się do zapisanej aktywności");
  }

  const now = new Date().toISOString();
  const doneCount = item.exercises.filter((exercise) => exercise.done).length;
  const trainingStatus =
    doneCount === 0
      ? TrainingStatus.REJECTED
      : doneCount === item.exercises.length
        ? TrainingStatus.FULFILLED
        : TrainingStatus.PARTIALLY_FULFILLED;

  const data = {
    rate,
    comment,
    programId,
    workoutId: item.workoutId,
    activityId: item.activityId,
    trainingStatus,
  };

  // Ponowna ocena aktualizuje istniejący pomiar — klientka też nie dostaje drugiej gwiazdki
  // do tego samego treningu, tylko zmienia poprzednią.
  if (item.feedbackId) {
    // Snapshot musi być CAŁYM poprzednim payloadem. Sklejanie go z nowego `data` i starej oceny
    // gubiło komentarz i status klientki — czyszczenie przywracałoby wtedy wartości, których
    // nigdy nie było.
    const { data: previous, error: previousError } = await supabase
      .from("measurement")
      .select("data")
      .eq("id", item.feedbackId)
      .maybeSingle();

    if (previousError) {
      throw new Error(`Nie udało się odczytać poprzedniej oceny: ${previousError.message}`);
    }

    const { error } = await supabase
      .from("measurement")
      .update({ data, updatedAt: now } as never)
      .eq("id", item.feedbackId);

    if (error) {
      throw new Error(error.message);
    }

    await logSimulatorWrite({
      traineeId,
      createdBy,
      tableName: SIMULATOR_TABLE.measurement,
      rowId: item.feedbackId,
      eventDate: date,
      mode,
      op: "update",
      before: previous ? { data: previous.data } : undefined,
      label: `Ocena treningu: ${item.name}`,
    });
    return;
  }

  const feedbackId = generateUniqId();

  const { error } = await supabase.from("measurement").insert({
    id: feedbackId,
    type: MeasurementType.FeedbackAboutTraining,
    data,
    eventDate: date,
    eventTimestamp: dayjs(date, "YYYY-MM-DD").hour(12).toISOString(),
    traineeId,
    userId,
    creator: AuthorTypeEnum.Client,
    createdAt: now,
    updatedAt: now,
  } as never);

  if (error) {
    throw new Error(error.message);
  }

  const { error: linkError } = await supabase
    .from("client_workout_activity")
    .update({ feedbackId, updatedAt: now })
    .eq("id", item.activityId);

  if (linkError) {
    throw new Error(linkError.message);
  }

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.measurement,
    rowId: feedbackId,
    eventDate: date,
    mode,
    op: "insert",
    label: `Ocena treningu: ${item.name}`,
  });
};

/** Cofa ocenę: kasuje pomiar i odpina go od aktywności. */
export const clearWorkoutRate = async (item: SimulatorWorkout): Promise<void> => {
  if (!item.feedbackId) {
    return;
  }

  const entry = await getSimulatorWrite(SIMULATOR_TABLE.measurement, item.feedbackId);

  // Ocenę wystawioną przez klientkę usunięcie skasowałoby bez śladu — po DELETE rejestr nie ma
  // czego odtworzyć.
  if (entry && !wasCreatedBySimulator(entry)) {
    throw new Error(
      "Tę ocenę wystawiła klientka — symulator jej nie kasuje. Użyj „wyczyść dane symulatora”, żeby wrócić do jej stanu.",
    );
  }

  if (item.activityId) {
    await supabase
      .from("client_workout_activity")
      .update({ feedbackId: null, updatedAt: new Date().toISOString() })
      .eq("id", item.activityId);
  }

  const { error } = await supabase.from("measurement").delete().eq("id", item.feedbackId);

  if (error) {
    throw new Error(error.message);
  }

  await forgetSimulatorWrite(SIMULATOR_TABLE.measurement, item.feedbackId);
};
